// src/components/nexilink/info-box.tsx
// Domain-specific info boxes built on shadcn/ui Alert
import React, { ReactNode } from 'react';
import { LucideIcon, Info, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

export type InfoVariant = 'info' | 'success' | 'warning' | 'error';

interface InfoBoxProps {
    title?: string;
    children: ReactNode;
    variant?: InfoVariant;
    icon?: LucideIcon;
    className?: string;
}

const variantConfig = {
    info: {
        container: 'border-blue-200 bg-blue-50 text-blue-900 [&>svg]:text-blue-600',
        iconBg: 'bg-blue-100',
        defaultIcon: Info
    },
    success: {
        container: 'border-green-200 bg-green-50 text-green-900 [&>svg]:text-green-600',
        iconBg: 'bg-green-100',
        defaultIcon: CheckCircle
    },
    warning: {
        container: 'border-yellow-200 bg-yellow-50 text-yellow-900 [&>svg]:text-yellow-600',
        iconBg: 'bg-yellow-100',
        defaultIcon: AlertTriangle
    },
    error: {
        container: 'border-red-200 bg-red-50 text-red-900 [&>svg]:text-red-600',
        iconBg: 'bg-red-100',
        defaultIcon: XCircle
    }
};

export function InfoBox({
    title,
    children,
    variant = 'info',
    icon,
    className
}: InfoBoxProps) {
    const config = variantConfig[variant];
    const Icon = icon || config.defaultIcon;

    return (
        <Alert className={cn('rounded-2xl', config.container, className)}>
            <div className="flex items-start gap-3">
                <div className={cn('p-2 rounded-lg flex-shrink-0', config.iconBg)}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    {title && <AlertTitle className="mb-2">{title}</AlertTitle>}
                    <AlertDescription className="text-sm">
                        {children}
                    </AlertDescription>
                </div>
            </div>
        </Alert>
    );
}

// Tips Box (commonly used for helpful hints)
interface TipsBoxProps {
    title?: string;
    tips: string[];
    className?: string;
}

export function TipsBox({
    title = 'Tips',
    tips,
    className
}: TipsBoxProps) {
    return (
        <InfoBox title={title} variant="info" className={className}>
            <ul className="space-y-3 mt-2">
                {tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold mt-0.5 flex-shrink-0">•</span>
                        <span>{tip}</span>
                    </li>
                ))}
            </ul>
        </InfoBox>
    );
}

// Gradient Info Box (for prominent info sections)
interface GradientInfoBoxProps {
    title: string;
    children: ReactNode;
    icon?: LucideIcon;
    className?: string;
}

export function GradientInfoBox({
    title,
    children,
    icon: Icon,
    className
}: GradientInfoBoxProps) {
    return (
        <div className={cn(
            'p-6 bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl border border-teal-100',
            className
        )}>
            <div className="flex items-start gap-3 mb-4">
                {Icon && (
                    <div className="p-2 bg-teal-100 rounded-lg">
                        <Icon className="w-5 h-5 text-teal-600" />
                    </div>
                )}
                <h3 className="font-semibold text-lg">
                    {title}
                </h3>
            </div>
            <div className="text-sm text-gray-700">
                {children}
            </div>
        </div>
    );
}
